import {
  AlertTriangle,
  ArrowRight,
  BadgeDollarSign,
  FileText,
  Lock,
  Scale,
  ServerCrash,
} from 'lucide-react';
import { getBranding } from '../../config/branding';
import { currentRuntime } from '../../config/runtime';
import type { PublicRoutePath } from './publicRoutes';

type LegalPageProps = {
  onNavigate?: (path: PublicRoutePath, options?: { hash?: string | null }) => void;
};

const LEGAL_SECTION_LINKS = [
  { id: 'terms', label: 'Terms' },
  { id: 'privacy', label: 'Privacy' },
  { id: 'billing-subscriptions', label: 'Billing' },
  { id: 'refund', label: 'Refund policy' },
  { id: 'market-data', label: 'Market data' },
  { id: 'risk-disclaimer', label: 'Risk' },
  { id: 'platform-availability', label: 'Availability' },
  { id: 'policy-questions', label: 'Contact' },
] as const;

const buildTermsPoints = (brandName: string) => [
  `${brandName} is intended for lawful trading journal, analytics, replay, and review workflows.`,
  'You are responsible for keeping your login credentials secure and for activity that occurs under your account.',
  'You may not misuse the service, attempt unauthorized access, interfere with normal operation, or upload data you do not have the right to use.',
  'Features, pricing, workflows, and supported integrations may change as the platform evolves.',
  'Access may be limited, suspended, or terminated if use is abusive, unsafe, fraudulent, or inconsistent with platform rules.',
];

const buildPrivacyPoints = (brandName: string) => [
  `${brandName} collects account, usage, and platform interaction data needed to operate the service and support core product workflows.`,
  'Imported files and uploaded trade data are used to power chart review, replay, analytics, debugging, and related platform functionality.',
  'This page is a high-level privacy summary and is not a substitute for a future dedicated privacy policy or expanded documentation.',
  'Questions about privacy handling should be directed through the published contact channel listed below.',
];

const buildBillingPoints = (brandName: string) => [
  `${brandName} public pricing currently references a 7-day trial together with monthly and annual subscription options.`,
  'Where a checkout flow creates a recurring subscription, billing continues until the subscription is canceled through the available billing flow.',
  'You are responsible for reviewing your subscription status, renewal timing, and cancellation settings in your account or billing portal.',
  'Cancellations apply prospectively based on the billing state tied to your account and do not retroactively rewrite prior usage or access periods.',
  'Refund policy details are listed in the dedicated refund section below.',
];

const buildRefundParagraphs = (brandName: string) => {
  const resolvedBrandName = String(brandName || '').trim() || 'The platform';
  const isTradeProof = resolvedBrandName.toLowerCase() === 'trade-proof';

  return [
    isTradeProof
      ? 'Trade-Proof provides a 7-day free trial so users can evaluate the platform before being charged.'
      : 'The platform provides a 7-day free trial so users can evaluate the platform before being charged.',
    'Once the trial period ends and a subscription payment is processed, all payments are final and non-refundable.',
    'Subscriptions can be canceled at any time to prevent future charges. Canceling does not retroactively refund prior payments.',
    isTradeProof
      ? 'In exceptional circumstances, Trade-Proof may review and grant refunds at its sole discretion.'
      : 'In exceptional circumstances, the platform may review and grant refunds at its sole discretion.',
  ];
};

const buildAvailabilityPoints = (brandName: string) => [
  `${brandName} is provided on an as-available basis and may change over time as infrastructure, integrations, and workflows are refined.`,
  'Interruptions, bugs, delays, maintenance windows, or incomplete functionality may occur, especially around evolving product areas.',
  'You should not rely on uninterrupted access, instant availability, or error-free operation for time-sensitive trading or compliance needs.',
  'Maintain your own records and use independent judgment rather than treating the platform as a guaranteed system of record.',
];

const buildMarketDataPoints = (brandName: string) => [
  'Replay and chart views may use historical or delayed market data from third-party providers and supported broker or local data connections.',
  'Market data availability can vary by symbol, market, timeframe, provider availability, account limits, and imported trade detail.',
  'Futures replay is not currently supported.',
  `Do not rely on ${brandName} market data alone for live trading decisions.`,
];

const buildRiskPoints = (brandName: string) => [
  `${brandName} provides analytics, journaling, review, and replay tooling only. It does not act as a broker, adviser, fiduciary, or execution venue.`,
  'Any trade ideas, annotations, journal notes, or metrics shown in the platform are informational and retrospective.',
  'Users are solely responsible for deciding whether any trading or investment action is appropriate for their own objectives, capital, experience level, and risk tolerance.',
  'Historical wins, losses, expectancy figures, replay outcomes, and behavioral observations do not predict future returns.',
];

type SectionCardProps = {
  id: string;
  eyebrow: string;
  title: string;
  icon: JSX.Element;
  paragraphs: string[];
  points: readonly string[];
};

function SectionCard({ id, eyebrow, title, icon, paragraphs, points }: SectionCardProps) {
  return (
    <section id={id} className="reveal workstation-frame rounded-[2.5rem] border border-zinc-200 bg-white p-8 text-[#0F172A] md:p-10" style={{ scrollMarginTop: '96px' }}>
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <div className="mb-3 text-[11px] font-bold uppercase tracking-[0.35em] text-pink-500">{eyebrow}</div>
          <h2 className="text-3xl font-bold tracking-tight text-zinc-900 md:text-4xl">{title}</h2>
        </div>
        <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl border border-zinc-200 bg-zinc-50 text-pink-500 shadow-sm">
          {icon}
        </div>
      </div>

      <div className="space-y-4">
        {paragraphs.map((paragraph) => (
          <p key={paragraph} className="max-w-3xl text-sm font-normal leading-7 text-zinc-500 md:text-[15px]">
            {paragraph}
          </p>
        ))}
      </div>

      {points.length > 0 ? (
        <div className="mt-8 grid gap-4">
          {points.map((point) => (
            <div key={point} className="rounded-[1.5rem] border border-zinc-200 bg-zinc-50 px-5 py-4 text-sm font-normal leading-7 text-zinc-600">
              {point}
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
}

export default function LegalPage({ onNavigate }: LegalPageProps) {
  const branding = getBranding(currentRuntime.brand.id);
  const brandName = branding.appName || branding.companyName || 'Trade-Proof';

  const termsPoints = buildTermsPoints(brandName);
  const privacyPoints = buildPrivacyPoints(brandName);
  const billingPoints = buildBillingPoints(brandName);
  const refundParagraphs = buildRefundParagraphs(brandName);
  const availabilityPoints = buildAvailabilityPoints(brandName);
  const marketDataPoints = buildMarketDataPoints(brandName);
  const riskPoints = buildRiskPoints(brandName);

  return (
    <div className="mx-auto max-w-6xl px-8 pb-16 pt-48 text-left text-[#0F172A]">
      <section className="reveal mb-10 grid gap-8 lg:grid-cols-[1.08fr_0.92fr] lg:items-start">
        <div>
          <div className="mb-5 text-[11px] font-bold uppercase tracking-[0.45em] text-pink-500">Public Legal Hub</div>
          <h1 className="mb-6 text-5xl font-bold tracking-tight text-zinc-900 md:text-5xl">Legal</h1>
          <p className="max-w-5xl text-lg font-normal leading-relaxed text-zinc-500">
            This page brings together the core legal terms, billing disclosures, privacy summary, and risk
            disclaimers that apply to using {brandName}. It is written as a launch-ready public overview so
            users can quickly understand the platform rules and key expectations before relying on the service.
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            {LEGAL_SECTION_LINKS.map((link) => (
              <a
                key={link.id}
                href={`#${link.id}`}
                className="rounded-full border border-zinc-200 bg-white px-5 py-2 text-[11px] font-bold uppercase tracking-[0.24em] text-zinc-500 transition-colors hover:border-pink-300 hover:text-pink-600"
              >
                {link.label}
              </a>
            ))}
          </div>
        </div>

        <div className="reveal workstation-frame rounded-[2.5rem] border border-zinc-200 bg-zinc-50 p-8 md:p-10">
          <div className="mb-4 text-[11px] font-bold uppercase tracking-[0.35em] text-zinc-400">Launch Summary</div>
          <div className="space-y-4">
            <div className="rounded-[1.5rem] border border-zinc-200 bg-white px-5 py-4">
              <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-zinc-400">Platform scope</div>
              <div className="mt-2 text-base font-bold text-zinc-900">Analytics, journaling, import, and replay tools</div>
            </div>
            <div className="rounded-[1.5rem] border border-zinc-200 bg-white px-5 py-4">
              <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-zinc-400">Current public billing</div>
              <div className="mt-2 text-base font-bold text-zinc-900">7-day trial, $39 monthly, $390 yearly</div>
            </div>
            <div className="rounded-[1.5rem] border border-zinc-200 bg-white px-5 py-4">
              <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-zinc-400">Important</div>
              <div className="mt-2 text-base font-bold text-zinc-900">{brandName} does not provide financial, investment, tax, or legal advice.</div>
            </div>
          </div>
        </div>
      </section>

      <section className="reveal mb-10 rounded-[2.5rem] border border-amber-200 bg-[linear-gradient(135deg,rgba(255,251,235,1),rgba(255,255,255,0.96))] p-8 text-[#0F172A] shadow-[0_18px_45px_rgba(15,23,42,0.06)] md:p-10">
        <div className="mb-4 flex items-center gap-3 text-amber-700">
          <AlertTriangle size={20} strokeWidth={2.4} />
          <span className="text-[11px] font-bold uppercase tracking-[0.35em]">Important Disclaimer</span>
        </div>
        <h2 className="max-w-4xl text-3xl font-bold tracking-tight text-zinc-900 md:text-4xl">
          {brandName} is a review and analytics platform.
          <span className="block">It is not an advice product.</span>
        </h2>
        <p className="mt-5 max-w-4xl text-base font-normal leading-8 text-zinc-600">
          The platform helps users organize trades, inspect execution behavior, replay market action, and review
          historical performance. Nothing on {brandName} should be treated as financial, investment, tax, or legal
          advice, and no analytics output should be understood as a recommendation to enter, exit, hold, size, or
          structure a trade.
        </p>
        <div className="mt-6 grid gap-4 md:grid-cols-2">
          <div className="rounded-[1.5rem] border border-amber-200/80 bg-white px-5 py-4 text-sm font-normal leading-7 text-zinc-600">
            You remain solely responsible for your trading and investment decisions, including risk management,
            execution, suitability, compliance, and tax treatment.
          </div>
          <div className="rounded-[1.5rem] border border-amber-200/80 bg-white px-5 py-4 text-sm font-normal leading-7 text-zinc-600">
            Past results, historical analytics, replay observations, and behavioral patterns do not guarantee future
            performance or outcomes.
          </div>
        </div>
      </section>

      <div className="grid gap-8">
        <SectionCard
          id="terms"
          eyebrow="Terms of Use"
          title="Terms of Service summary"
          icon={<FileText size={20} strokeWidth={2.2} />}
          paragraphs={[
            `${brandName} is a software platform and its rules are intended to keep access orderly, secure, and compatible with the product's intended use.`,
            'This summary is designed to set clear expectations at launch without overstating legal commitments that have not been formally published elsewhere.',
          ]}
          points={termsPoints}
        />

        <SectionCard
          id="privacy"
          eyebrow="Privacy"
          title="Privacy Policy summary"
          icon={<Lock size={20} strokeWidth={2.2} />}
          paragraphs={[
            `${brandName} needs certain account and platform data to deliver imports, replay, analytics, billing, and support workflows.`,
            'Because this page is a legal hub rather than a full policy library, the statements below stay intentionally high-level and focused on core operational use.',
          ]}
          points={privacyPoints}
        />

        <SectionCard
          id="billing-subscriptions"
          eyebrow="Billing"
          title="Billing and subscriptions"
          icon={<BadgeDollarSign size={20} strokeWidth={2.2} />}
          paragraphs={[
            `${brandName} publicly references a trial and paid subscription access model, and the application also exposes account-level billing state and cancellation-at-period-end behavior.`,
            'The goal here is clarity: users should understand when recurring access can continue, what cancellation changes, and what this page does not promise.',
          ]}
          points={billingPoints}
        />

        <div id="refund" style={{ scrollMarginTop: '96px' }}>
          <SectionCard
            id="refund-policy"
            eyebrow="Refund Policy"
            title="Refund and cancellation policy"
            icon={<BadgeDollarSign size={20} strokeWidth={2.2} />}
            paragraphs={refundParagraphs}
            points={[]}
          />
        </div>

        <SectionCard
          id="market-data"
          eyebrow="Market Data"
          title="Market data and replay availability"
          icon={<ServerCrash size={20} strokeWidth={2.2} />}
          paragraphs={[
            `${brandName} uses market data only to support trade review, chart context, and replay workflows.`,
            'This section is included so users understand that chart and replay availability can depend on data providers, broker connections, and the detail available in imported records.',
          ]}
          points={marketDataPoints}
        />

        <SectionCard
          id="platform-availability"
          eyebrow="Availability"
          title="Platform availability and limitations"
          icon={<ServerCrash size={20} strokeWidth={2.2} />}
          paragraphs={[
            `${brandName} is a live product that may continue to evolve after launch. Users should expect normal software limitations and operational change over time.`,
            'This section is intentionally direct so users do not misread the platform as an uninterrupted or guaranteed execution-critical service.',
          ]}
          points={availabilityPoints}
        />

        <section id="risk-disclaimer" className="reveal workstation-frame rounded-[2.5rem] border border-zinc-200 bg-white p-8 text-[#0F172A] md:p-10">
          <div className="mb-6 flex items-start justify-between gap-4">
            <div>
              <div className="mb-3 text-[11px] font-bold uppercase tracking-[0.35em] text-pink-500">Risk and Responsibility</div>
              <h2 className="text-3xl font-bold tracking-tight text-zinc-900 md:text-4xl">No-advice and user-responsibility disclaimer</h2>
            </div>
            <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl border border-zinc-200 bg-zinc-50 text-pink-500 shadow-sm">
              <Scale size={20} strokeWidth={2.2} />
            </div>
          </div>

          <div className="grid gap-5 md:grid-cols-2">
            {riskPoints.map((point) => (
              <div key={point} className="rounded-[1.5rem] border border-zinc-200 bg-zinc-50 px-5 py-4 text-sm font-normal leading-7 text-zinc-600">
                {point}
              </div>
            ))}
          </div>
        </section>

        <section id="policy-questions" className="reveal workstation-frame rounded-[2.5rem] border border-zinc-200 bg-zinc-100 p-8 text-[#0F172A] md:p-10">
          <div className="mb-6 flex items-start justify-between gap-4">
            <div>
              <div className="mb-3 text-[11px] font-bold uppercase tracking-[0.35em] text-pink-500">Contact</div>
              <h2 className="text-3xl font-bold tracking-tight text-zinc-900 md:text-4xl">Legal and policy questions</h2>
            </div>
            <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl border border-zinc-200 bg-white text-pink-500 shadow-sm">
              <ArrowRight size={20} strokeWidth={2.2} />
            </div>
          </div>

          <p className="max-w-3xl text-sm font-normal leading-7 text-zinc-600 md:text-[15px]">
            If you have a legal, policy, billing, or privacy-related question about {brandName}, use the public support contact
            workflow so the request can be routed appropriately.
          </p>

          <div className="mt-8 grid gap-5 lg:grid-cols-[1fr_auto] lg:items-center">
            <div className="rounded-[1.5rem] border border-zinc-200 bg-white px-5 py-4 text-sm font-normal leading-7 text-zinc-600">
              Use the public contact form for legal or privacy questions and choose Legal Inquiry or Privacy Request.
              Those messages route directly to the appropriate inbox.
            </div>
            <button
              type="button"
              onClick={() => onNavigate?.('/help', { hash: 'contact' })}
              className="group inline-flex items-center justify-center gap-3 whitespace-nowrap rounded-full bg-[#0F172A] px-7 py-3 text-[11px] font-bold uppercase tracking-[0.24em] text-white shadow-xl transition-all active:scale-95 hover:bg-black"
            >
              Open Contact Form <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}
